import type { CommitmentPricing, PaygPricing } from "@/modules/billing/linkedResources.ts";
import { atomAllLocationsQuery } from "@/modules/location/atomAllLocationsQuery.ts";
import type { MetalCommitmentWithEndsAt } from "@/modules/metal/commitment/commitment.ts";
import { getCurrentCommitment } from "@/modules/metal/commitment/commitment.ts";
import { commitmentLocationQueryAtom } from "@/modules/metal/commitment/commitmentApi.ts";
import type { MetalListItemWithType } from "@/modules/metal/metalQuery.ts";
import { metalLocationQueryAtom } from "@/modules/metal/metalQuery.ts";
import { pricesAtom } from "@/modules/pricing/pricingQuery.ts";
import type { Price } from "@/modules/pricing/resourcePricing.ts";
import { getBareMetalSpecPrice } from "@/modules/pricing/resourcePricing.ts";
import { mergeLoadedQueries, type QMergedResult } from "@/utils/query/mergeQueries.ts";
import type { MetalListItem } from "@warrenio/api-spec/spec.oats.gen";
import { mapOnValues, mergeMaps } from "@warrenio/utils/collections/maps";
import { atomFamily } from "jotai-family";
import { atom } from "jotai/vanilla";

export interface MetalListItemWithPrice extends MetalListItemWithType {
    pricing: PaygPricing | CommitmentPricing;
}

export type MetalPricedResponse = Map<MetalListItem["uuid"], MetalListItemWithPrice>;

function getMetalPricing(
    price: Price,
    currentCommitment: MetalCommitmentWithEndsAt | null,
): PaygPricing | CommitmentPricing {
    if (currentCommitment == null) {
        return { pricingType: "payg", price };
    }

    return {
        pricingType: "commitment",
        price: { hourly: Number(currentCommitment.price) / currentCommitment.period_hours },
        commitment: currentCommitment,
    };
}

export const metalPricedQueryAtom = atomFamily((location: string) =>
    atom((get): QMergedResult<MetalPricedResponse | undefined> => {
        return mergeLoadedQueries(
            {
                items: get(metalLocationQueryAtom(location)),
                commitments: get(commitmentLocationQueryAtom(location)),
                prices: get(pricesAtom),
            },
            ({ items, commitments, prices }) =>
                mapOnValues(items, (item): MetalListItemWithPrice => {
                    const commitment = getCurrentCommitment(commitments, item.uuid);
                    const price = getBareMetalSpecPrice(prices, item.location, item.spec.uuid);

                    return {
                        ...item,
                        pricing: getMetalPricing(price, commitment),
                    };
                }),
        );
    }),
);

export const allMetalPricedQueryAtom = atomAllLocationsQuery(metalPricedQueryAtom, (results) => mergeMaps(results));
